<script setup>
import { ref, computed } from 'vue';
import { useVuelidate } from '@vuelidate/core';
import { required, minLength } from '@vuelidate/validators';
import { useStore, useMapGetter } from 'dashboard/composables/store';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import Button from 'dashboard/components-next/button/Button.vue';
import AgentAPI from 'dashboard/api/agents';
import wootConstants from 'dashboard/constants/globals';

const props = defineProps({
  id: {
    type: Number,
    required: true,
  },
  name: {
    type: String,
    required: true,
  },
  email: {
    type: String,
    default: '',
  },
  type: {
    type: String,
    default: '',
  },
  availability: {
    type: String,
    default: '',
  },
  provider: {
    type: String,
    default: '',
  },
  customRoleId: {
    type: Number,
    default: null,
  },
});

const emit = defineEmits(['close']);

const { AVAILABILITY_STATUS_KEYS } = wootConstants;

const store = useStore();
const { t } = useI18n();

const agentName = ref(props.name);
const agentAvailability = ref(props.availability);
const selectedRoleId = ref(props.customRoleId || props.type);
const agentPassword = ref('');
const agentPasswordConfirmation = ref('');
const isUpdatingPassword = ref(false);

const rules = {
  agentName: { required, minLength: minLength(1) },
  selectedRoleId: { required },
  agentAvailability: { required },
};

const v$ = useVuelidate(rules, {
  agentName,
  selectedRoleId,
  agentAvailability,
});

const passwordRules = {
  agentPassword: { required, minLength: minLength(6) },
  agentPasswordConfirmation: {
    required,
    minLength: minLength(6),
    matchesPassword: value => value === agentPassword.value,
  },
};

const passwordV$ = useVuelidate(passwordRules, {
  agentPassword,
  agentPasswordConfirmation,
});

const pageTitle = computed(
  () => `${t('AGENT_MGMT.EDIT.TITLE')} - ${props.name}`
);
const passwordDescription = computed(() =>
  t('AGENT_MGMT.EDIT.PASSWORD_RESET.DESCRIPTION', { email: props.email })
);

const uiFlags = useMapGetter('agents/getUIFlags');
const getCustomRoles = useMapGetter('customRole/getCustomRoles');

const roles = computed(() => {
  const defaultRoles = [
    {
      id: 'administrator',
      name: 'administrator',
      label: t('AGENT_MGMT.AGENT_TYPES.ADMINISTRATOR'),
    },
    {
      id: 'agent',
      name: 'agent',
      label: t('AGENT_MGMT.AGENT_TYPES.AGENT'),
    },
  ];

  const customRoles = getCustomRoles.value.map(role => ({
    id: role.id,
    name: `custom_${role.id}`,
    label: role.name,
  }));

  return [...defaultRoles, ...customRoles];
});

const selectedRole = computed(() =>
  roles.value.find(
    role =>
      role.id === selectedRoleId.value || role.name === selectedRoleId.value
  )
);

const statusList = computed(() => {
  return [
    t('PROFILE_SETTINGS.FORM.AVAILABILITY.STATUS.ONLINE'),
    t('PROFILE_SETTINGS.FORM.AVAILABILITY.STATUS.BUSY'),
    t('PROFILE_SETTINGS.FORM.AVAILABILITY.STATUS.OFFLINE'),
  ];
});

const availabilityStatuses = computed(() =>
  statusList.value.map((statusLabel, index) => ({
    label: statusLabel,
    value: AVAILABILITY_STATUS_KEYS[index],
    disabled: props.availability === AVAILABILITY_STATUS_KEYS[index],
  }))
);

const editAgent = async () => {
  v$.value.$touch();
  if (v$.value.$invalid) return;

  try {
    const payload = {
      id: props.id,
      name: agentName.value,
      availability: agentAvailability.value,
    };

    if (selectedRole.value.name.startsWith('custom_')) {
      payload.custom_role_id = selectedRole.value.id;
    } else {
      payload.role = selectedRole.value.name;
      payload.custom_role_id = null;
    }

    await store.dispatch('agents/update', payload);
    useAlert(t('AGENT_MGMT.EDIT.API.SUCCESS_MESSAGE'));
    emit('close');
  } catch (error) {
    useAlert(t('AGENT_MGMT.EDIT.API.ERROR_MESSAGE'));
  }
};

const updatePassword = async () => {
  passwordV$.value.$touch();
  if (passwordV$.value.$invalid) return;

  try {
    isUpdatingPassword.value = true;
    await AgentAPI.updatePassword(props.id, {
      password: agentPassword.value,
      password_confirmation: agentPasswordConfirmation.value,
    });
    agentPassword.value = '';
    agentPasswordConfirmation.value = '';
    passwordV$.value.$reset();
    useAlert(t('AGENT_MGMT.EDIT.PASSWORD_RESET.ADMIN_SUCCESS_MESSAGE'));
  } catch (error) {
    const errorMessage =
      error?.response?.data?.error ||
      t('AGENT_MGMT.EDIT.PASSWORD_RESET.ERROR_MESSAGE');
    useAlert(errorMessage);
  } finally {
    isUpdatingPassword.value = false;
  }
};
</script>

<template>
  <div class="flex flex-col h-auto overflow-auto">
    <woot-modal-header :header-title="pageTitle" />
    <form class="w-full" @submit.prevent="editAgent">
      <div class="w-full">
        <label :class="{ error: v$.agentName.$error }">
          {{ $t('AGENT_MGMT.EDIT.FORM.NAME.LABEL') }}
          <input
            v-model="agentName"
            type="text"
            :placeholder="$t('AGENT_MGMT.EDIT.FORM.NAME.PLACEHOLDER')"
            @input="v$.agentName.$touch"
          />
        </label>
      </div>

      <div class="w-full">
        <label :class="{ error: v$.selectedRoleId.$error }">
          {{ $t('AGENT_MGMT.EDIT.FORM.AGENT_TYPE.LABEL') }}
          <select v-model="selectedRoleId" @change="v$.selectedRoleId.$touch">
            <option v-for="role in roles" :key="role.id" :value="role.id">
              {{ role.label }}
            </option>
          </select>
          <span v-if="v$.selectedRoleId.$error" class="message">
            {{ $t('AGENT_MGMT.EDIT.FORM.AGENT_TYPE.ERROR') }}
          </span>
        </label>
      </div>

      <div class="w-full">
        <label :class="{ error: v$.agentAvailability.$error }">
          {{ $t('PROFILE_SETTINGS.FORM.AVAILABILITY.LABEL') }}
          <select
            v-model="agentAvailability"
            @change="v$.agentAvailability.$touch"
          >
            <option
              v-for="status in availabilityStatuses"
              :key="status.value"
              :value="status.value"
            >
              {{ status.label }}
            </option>
          </select>
          <span v-if="v$.agentAvailability.$error" class="message">
            {{ $t('AGENT_MGMT.EDIT.FORM.AGENT_AVAILABILITY.ERROR') }}
          </span>
        </label>
      </div>

      <div
        v-if="provider !== 'saml' && type !== 'administrator'"
        class="w-full pt-4 mt-2 border-t border-n-weak"
      >
        <h4 class="mb-1 text-base font-medium text-n-slate-12">
          {{ $t('AGENT_MGMT.EDIT.PASSWORD_RESET.TITLE') }}
        </h4>
        <p class="mb-3 text-sm text-n-slate-11">
          {{ passwordDescription }}
        </p>

        <label :class="{ error: passwordV$.agentPassword.$error }">
          {{ $t('AGENT_MGMT.EDIT.PASSWORD_RESET.PASSWORD.LABEL') }}
          <input
            v-model="agentPassword"
            type="password"
            autocomplete="new-password"
            :placeholder="
              $t('AGENT_MGMT.EDIT.PASSWORD_RESET.PASSWORD.PLACEHOLDER')
            "
            @input="passwordV$.agentPassword.$touch"
          />
          <span v-if="passwordV$.agentPassword.$error" class="message">
            {{ $t('AGENT_MGMT.EDIT.PASSWORD_RESET.PASSWORD.ERROR') }}
          </span>
        </label>

        <label :class="{ error: passwordV$.agentPasswordConfirmation.$error }">
          {{ $t('AGENT_MGMT.EDIT.PASSWORD_RESET.CONFIRMATION.LABEL') }}
          <input
            v-model="agentPasswordConfirmation"
            type="password"
            autocomplete="new-password"
            :placeholder="
              $t('AGENT_MGMT.EDIT.PASSWORD_RESET.CONFIRMATION.PLACEHOLDER')
            "
            @input="passwordV$.agentPasswordConfirmation.$touch"
          />
          <span
            v-if="passwordV$.agentPasswordConfirmation.$error"
            class="message"
          >
            {{ $t('AGENT_MGMT.EDIT.PASSWORD_RESET.CONFIRMATION.ERROR') }}
          </span>
        </label>

        <p class="mb-3 text-sm text-n-slate-11">
          {{ $t('AGENT_MGMT.EDIT.PASSWORD_RESET.REQUIREMENTS') }}
        </p>

        <Button
          type="button"
          icon="i-lucide-lock-keyhole"
          :label="$t('AGENT_MGMT.EDIT.PASSWORD_RESET.ADMIN_RESET_BUTTON')"
          :disabled="passwordV$.$invalid || isUpdatingPassword"
          :is-loading="isUpdatingPassword"
          @click.prevent="updatePassword"
        />
      </div>

      <div class="flex justify-end w-full gap-2 px-0 py-2">
        <div class="flex items-center justify-end gap-2">
          <Button
            faded
            slate
            type="reset"
            :label="$t('AGENT_MGMT.EDIT.CANCEL_BUTTON_TEXT')"
            @click.prevent="emit('close')"
          />
          <Button
            type="submit"
            :label="$t('AGENT_MGMT.EDIT.FORM.SUBMIT')"
            :disabled="v$.$invalid || uiFlags.isUpdating"
            :is-loading="uiFlags.isUpdating"
          />
        </div>
      </div>
    </form>
  </div>
</template>
